"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { PrefetchLink } from "@/components/PrefetchLink";
import styles from "./IndexList.module.scss";

export interface IndexRow {
  href: string;
  name: string;
  type: string;
  year: string;
  external?: boolean;
  /** Optional visual for the floating cursor preview; rows without one hide it. */
  image?: string;
  /** Optional pair of visuals shown as a two-up strip on mobile, where the
      cursor preview can't run. Desktop hides the strip and, since the images
      lazy-load, never fetches it. */
  mobileImages?: string[];
  /** Optional category rendered as data-cat, so /work's pre-paint URL
      stamps (html[data-oi-filter]) can hide non-matching rows in CSS. */
  cat?: string;
}

/* The preview's center trails the cursor to its right, clamped to the
   viewport. Values are px in cursor space. */
const PREVIEW_OFFSET_X = 156;
const PREVIEW_MARGIN = 16;

const clamp = (v: number, lo: number, hi: number) =>
  lo > hi ? (lo + hi) / 2 : Math.min(hi, Math.max(lo, v));

/**
 * The work inventory as a data table: Project | Type | Year rows on
 * hairlines, flooding to the inverted surface on hover. Name sits in the
 * left half, Type starts at the 50% seam. Shared by the homepage index
 * and the /work list view.
 *
 * Rows carrying an `image` feed the floating preview: a fixed card that
 * lerps after the cursor, tilts with its horizontal velocity, and wipes
 * between visuals in the direction of travel through the list. Fine
 * pointers only, gated behind prefers-reduced-motion.
 */
export function IndexList({
  rows,
  ariaLabel,
}: {
  rows: IndexRow[];
  ariaLabel?: string;
}) {
  const previewSrcs = useMemo(
    () => [...new Set(rows.flatMap((row) => (row.image ? [row.image] : [])))],
    [rows],
  );

  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (previewSrcs.length === 0) return;
    const mq = window.matchMedia(
      "(pointer: fine) and (prefers-reduced-motion: no-preference)",
    );
    const update = () => setEnabled(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [previewSrcs.length]);

  const [active, setActive] = useState<{
    src: string;
    dir: 1 | -1;
    tick: number;
  } | null>(null);

  const previewRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const activeSrcRef = useRef<string | null>(null);
  /* Per-src activation stamp: doubles as z-order (latest on top) and as the
     key suffix that remounts an image only when it re-activates, so its
     wipe animation restarts without disturbing the layers beneath. */
  const stampRef = useRef(new Map<string, number>());
  const tickRef = useRef(0);
  const lastRowRef = useRef(-1);
  const pos = useRef({
    x: 0,
    y: 0,
    tx: 0,
    ty: 0,
    rot: 0,
    w: 0,
    h: 0,
    raf: 0,
    last: 0,
    snapped: false,
  });

  useEffect(() => {
    const s = pos.current;
    return () => {
      if (s.raf) cancelAnimationFrame(s.raf);
      s.raf = 0;
    };
  }, []);

  const goal = () => {
    const s = pos.current;
    return [
      clamp(
        s.tx + PREVIEW_OFFSET_X,
        PREVIEW_MARGIN + s.w / 2,
        window.innerWidth - PREVIEW_MARGIN - s.w / 2,
      ),
      clamp(
        s.ty,
        PREVIEW_MARGIN + s.h / 2,
        window.innerHeight - PREVIEW_MARGIN - s.h / 2,
      ),
    ];
  };

  /* Follow factors are tuned per 60Hz frame; `k` rescales them by the real
     frame time so a 120Hz display trails at the same wall-clock speed. The
     tilt reads velocity per 60Hz frame for the same reason. */
  const step = (now: number) => {
    const s = pos.current;
    const el = previewRef.current;
    if (!el) {
      s.raf = 0;
      return;
    }
    const dt = s.last ? Math.min(64, now - s.last) : 1000 / 60;
    s.last = now;
    const k = (f: number) => 1 - (1 - f) ** ((dt * 60) / 1000);
    const [gx, gy] = goal();
    const px = s.x;
    s.x += (gx - s.x) * k(0.16);
    s.y += (gy - s.y) * k(0.13);
    const vx = ((s.x - px) * (1000 / 60)) / dt;
    s.rot += (clamp(vx * 0.55, -12, 12) - s.rot) * k(0.11);
    el.style.transform = `translate3d(${s.x}px, ${s.y}px, 0) rotate(${s.rot}deg)`;
    const resting =
      Math.abs(gx - s.x) < 0.2 &&
      Math.abs(gy - s.y) < 0.2 &&
      Math.abs(s.rot) < 0.05;
    s.raf = resting ? 0 : requestAnimationFrame(step);
    if (resting) s.last = 0;
  };

  const onMove = (e: React.PointerEvent) => {
    const s = pos.current;
    s.tx = e.clientX;
    s.ty = e.clientY;
    if (!s.snapped) {
      s.w = innerRef.current?.offsetWidth ?? 300;
      s.h = innerRef.current?.offsetHeight ?? 188;
      [s.x, s.y] = goal();
      s.rot = 0;
      s.snapped = true;
    }
    if (!s.raf) s.raf = requestAnimationFrame(step);
  };

  const onLeave = () => {
    if (previewRef.current) previewRef.current.dataset.on = "0";
    pos.current.snapped = false;
    lastRowRef.current = -1;
  };

  const activate = (row: IndexRow, index: number) => {
    const el = previewRef.current;
    if (el) el.dataset.on = row.image ? "1" : "0";
    const dir: 1 | -1 =
      lastRowRef.current < 0 || index >= lastRowRef.current ? 1 : -1;
    lastRowRef.current = index;
    if (!row.image || activeSrcRef.current === row.image) return;
    activeSrcRef.current = row.image;
    const tick = ++tickRef.current;
    stampRef.current.set(row.image, tick);
    setActive({ src: row.image, dir, tick });
  };

  return (
    <div
      className={styles.list}
      aria-label={ariaLabel}
      onPointerMove={enabled ? onMove : undefined}
      onPointerLeave={enabled ? onLeave : undefined}
    >
      {rows.map((row, index) => {
        const content = (
          <>
            <span className={styles.name}>
              {row.name}
              {row.external && (
                <span className={styles.ext} aria-hidden>
                  {" ↗︎"}
                </span>
              )}
            </span>
            <span className={styles.type}>{row.type}</span>
            <span className={styles.year}>{row.year}</span>
            {row.mobileImages && row.mobileImages.length > 0 && (
              <span className={styles.strip} aria-hidden>
                {row.mobileImages.slice(0, 2).map((src, _, pair) => (
                  <span key={src} className={styles.stripImg}>
                    <Image
                      src={src}
                      alt=""
                      fill
                      sizes={pair.length > 1 ? "50vw" : "100vw"}
                    />
                  </span>
                ))}
              </span>
            )}
          </>
        );
        const hover = enabled ? () => activate(row, index) : undefined;
        return row.external ? (
          <a
            key={`${row.href}-${row.name}`}
            className={styles.row}
            href={row.href}
            target="_blank"
            rel="noreferrer"
            onPointerEnter={hover}
            data-cat={row.cat}
          >
            {content}
          </a>
        ) : (
          <PrefetchLink
            key={`${row.href}-${row.name}`}
            className={styles.row}
            href={row.href}
            onPointerEnter={hover}
            data-cat={row.cat}
          >
            {content}
          </PrefetchLink>
        );
      })}
      {enabled && (
        <div
          className={styles.preview}
          ref={previewRef}
          aria-hidden
          data-on="0"
        >
          <div className={styles.previewInner} ref={innerRef}>
            {previewSrcs.map((src) => {
              const isActive = active?.src === src;
              return (
                <span
                  key={`${src}#${stampRef.current.get(src) ?? 0}`}
                  className={
                    isActive
                      ? `${styles.previewImg} ${styles.isActive}`
                      : styles.previewImg
                  }
                  style={{ zIndex: stampRef.current.get(src) ?? 0 }}
                  data-dir={isActive ? active.dir : undefined}
                >
                  <Image src={src} alt="" fill sizes="340px" />
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
